(function initBraceletSizing(root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.IWBraceletSizing = api;
}(typeof globalThis !== 'undefined' ? globalThis : this, function braceletSizingFactory() {
  // Kept in the public API for older callers that use it as a tray/layout
  // reference. Physical wrist sizing must not apply this visual floor: doing
  // so fixes the centreline at 130mm while thicker newly-added beads increase
  // the radial deduction, which can make the reported wrist size shrink.
  const MIN_CIRCUMFERENCE_MM = 130;
  const WRAP_CROSS_MM = 8;
  // Five inches is the minimum finished inner circumference for every
  // bracelet construction. Hardware paths reuse this threshold after
  // subtracting their own effective connector length.
  const ELASTIC_CLOSE_INNER_MM = 127;
  const THROUGH_PAIR_DISPLAY_SCALE_BY_SKU = Object.freeze({
    // The cylinder occupies about 178/512 of this square texture. At the
    // generic through-pair scale it renders at about 6.67mm; 0.90 brings the
    // cylinder diameter to about 6.00mm without changing its string seat.
    'pave-spacer-with-seven-point-star-charm-6.2mm': 0.90,
  });

  function positive(value, fallback) {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : fallback;
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function elasticCircleLayout({
    totalArcPx = 0,
    totalWidthMm = 0,
    avgThicknessMm = 0,
    closeInnerMm = ELASTIC_CLOSE_INNER_MM,
    maxRadiusPx = Infinity,
    minRadiusPx = 0,
    fallbackRadiusPx = 1,
  } = {}) {
    const arcPx = Math.max(0, Number(totalArcPx) || 0);
    const centerlineMm = Math.max(0, Number(totalWidthMm) || 0);
    const thicknessMm = Math.max(0, Number(avgThicknessMm) || 0);
    const closeInner = positive(closeInnerMm, ELASTIC_CLOSE_INNER_MM);
    const closeCenterlineMm = closeInner + Math.PI * thicknessMm;
    const rawProgress = closeCenterlineMm > 0 ? centerlineMm / closeCenterlineMm : 0;
    const progress = clamp(rawProgress, 0, 1);
    const fullCircle = Math.PI * 2;
    let naturalRadiusPx = progress > 1e-9
      ? arcPx / (fullCircle * progress)
      : positive(fallbackRadiusPx, 1);
    if (!(naturalRadiusPx > 0)) naturalRadiusPx = positive(fallbackRadiusPx, 1);
    const minimumRadiusPx = Math.max(0, Number(minRadiusPx) || 0);
    const pxPerMm = centerlineMm > 1e-9 ? arcPx / centerlineMm : 0;
    const closeNaturalRadiusPx = pxPerMm > 1e-9
      ? (pxPerMm * closeCenterlineMm) / fullCircle
      : naturalRadiusPx;
    let scale = minimumRadiusPx > 0 && closeNaturalRadiusPx > 0
      ? Math.max(1, minimumRadiusPx / closeNaturalRadiusPx)
      : 1;
    let desiredRadiusPx = naturalRadiusPx * scale;

    const radiusCap = Number(maxRadiusPx);
    if (Number.isFinite(radiusCap) && radiusCap > 0 && desiredRadiusPx > radiusCap) {
      scale *= radiusCap / desiredRadiusPx;
      desiredRadiusPx = radiusCap;
    }
    // Keep bead occupancy and ring circumference in the same coordinate
    // system. A minimum display radius must scale the beads and the path
    // together; enlarging only the path creates visible gaps at closure.
    return {
      radiusPx: desiredRadiusPx,
      totalAngle: fullCircle * progress,
      progress,
      closed: rawProgress >= 1,
      scale,
      closeCenterlineMm,
    };
  }

  function pendantSlotWidthMm(material) {
    const item = material && typeof material === 'object' ? material : {};
    const explicit = positive(
      item.slotWidthMm ?? item.slot_width_mm,
      positive(item.bailWidthMm ?? item.bail_width_mm, 0),
    );
    if (explicit > 0) return clamp(explicit, 3, 20);
    const size = positive(item.sizeMm, 8);
    const width = positive(item.widthMm, size);
    return clamp(width * 0.25, 5, 10);
  }

  function materialGeometry(material) {
    const item = material && typeof material === 'object' ? material : {};
    const size = positive(item.sizeMm, 8);
    const effectiveAlongMm = positive(item.effectiveAlongMm ?? item.effective_along_mm, 0);
    const attach = throughPairAttachment(item);
    if (throughPairUsesPhysicalBounds(item, attach)) {
      const width = positive(item.widthMm, size);
      const height = positive(item.heightMm, width);
      const cos = Math.abs(Math.cos(attach.axisRad));
      const sin = Math.abs(Math.sin(attach.axisRad));
      return {
        alongMm: effectiveAlongMm || (cos * width + sin * height),
        thicknessMm: sin * width + cos * height,
      };
    }
    const alongMm = item.shape === 'pendant'
      ? pendantSlotWidthMm(item)
      : (effectiveAlongMm || positive(item.widthMm, size));
    const thicknessMm = positive(item.heightMm, positive(item.widthMm, size));
    return { alongMm, thicknessMm };
  }

  function layoutHalfExtentPx(material, {
    radius = 0,
    visibleWidthPx = 0,
    visibleHeightPx = 0,
  } = {}) {
    const item = material && typeof material === 'object' ? material : {};
    const beadRadius = positive(radius, 0);
    const geometry = materialGeometry(item);
    const maxMm = Math.max(geometry.alongMm, geometry.thicknessMm, 1);
    const metadataHalf = beadRadius * (geometry.alongMm / maxMm);
    const textureAware = item.shape === 'freeform' || item.shape === 'spacer';
    if (!textureAware) return metadataHalf || beadRadius;

    const visibleHalf = positive(visibleWidthPx, metadataHalf * 2) / 2;
    if (item.shape === 'spacer') {
      if (visibleHalf <= metadataHalf) return metadataHalf || beadRadius;
      // Spacer widthMm remains the real along-string contribution used for
      // sizing. The front-facing product texture can be wider than that
      // physical slot, so reserve its visible alpha footprint for 2D layout
      // without changing the material's real dimensions. Neighbours sit on
      // rotated tangents, so retain the same corner projection allowance used
      // by other non-round textures.
      const radialHalf = Math.max(
        beadRadius * (geometry.thicknessMm / maxMm),
        positive(visibleHeightPx, beadRadius * 2) / 2,
      );
      const cornerAllowance = radialHalf * 0.06;
      const finishAllowance = Math.max(0.5, beadRadius * 0.02);
      return visibleHalf + cornerAllowance + finishAllowance;
    }

    const tangentHalf = Math.max(metadataHalf, visibleHalf);
    const radialHalf = Math.max(
      beadRadius * (geometry.thicknessMm / maxMm),
      positive(visibleHeightPx, beadRadius * 2) / 2,
    );
    // A non-round bead is seated tangent to the bracelet. Its neighbour lies
    // on a slightly rotated tangent, so square/rectangular corners project
    // beyond the raw horizontal half-width. Keep a small physical clearance
    // without changing the spacing of round beads.
    const cornerAllowance = radialHalf * 0.06;
    const finishAllowance = Math.max(0.5, beadRadius * 0.02);
    return tangentHalf + cornerAllowance + finishAllowance;
  }

  function detectPendantAnchorFromAlpha(
    data,
    width,
    height,
    {
      alphaThreshold = 40,
      topBandRatio = 0.25,
      connectorSeatDepthRatio = 0.11,
      connectorHoleMaxDepthRatio = 0.20,
    } = {},
  ) {
    const w = Math.max(0, Math.floor(Number(width) || 0));
    const h = Math.max(0, Math.floor(Number(height) || 0));
    if (!data || w < 1 || h < 1 || data.length < w * h * 4) return false;

    let minX = w;
    let minY = h;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        if (data[(y * w + x) * 4 + 3] > alphaThreshold) {
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
        }
      }
    }
    if (maxY < minY) return false;

    const visibleHeight = Math.max(1, maxY - minY + 1);
    const bandHeight = Math.max(3, Math.round(visibleHeight * clamp(topBandRatio, 0.1, 0.5)));
    const bandEnd = Math.min(maxY, minY + bandHeight - 1);
    let weightedX = 0;
    let totalAlpha = 0;
    for (let y = minY; y <= bandEnd; y += 1) {
      for (let x = 0; x < w; x += 1) {
        const alpha = data[(y * w + x) * 4 + 3];
        if (alpha <= alphaThreshold) continue;
        weightedX += (x + 0.5) * alpha;
        totalAlpha += alpha;
      }
    }
    if (!(totalAlpha > 0)) return false;
    const topU = clamp(weightedX / totalAlpha / w, 0, 1);

    // Look for a genuine transparent opening in the photographed connector.
    // The outer transparent background is ignored because it touches an image
    // edge. Only a small, enclosed opening near the top of the visible pendant
    // can become the OT-ring threading seat.
    const visited = new Uint8Array(w * h);
    const queue = new Int32Array(w * h);
    const connectorLimitY = Math.min(
      maxY,
      minY + Math.max(
        3,
        Math.round(visibleHeight * clamp(connectorHoleMaxDepthRatio, 0.08, 0.35)),
      ),
    );
    const holeCandidates = [];
    const enqueueComponent = (start) => {
      let head = 0;
      let tail = 0;
      queue[tail++] = start;
      visited[start] = 1;
      let area = 0;
      let sumX = 0;
      let sumY = 0;
      let touchesEdge = false;
      let componentMinY = h;
      let componentMaxY = -1;
      while (head < tail) {
        const index = queue[head++];
        const x = index % w;
        const y = Math.floor(index / w);
        area += 1;
        sumX += x + 0.5;
        sumY += y + 0.5;
        componentMinY = Math.min(componentMinY, y);
        componentMaxY = Math.max(componentMaxY, y);
        if (x === 0 || y === 0 || x === w - 1 || y === h - 1) touchesEdge = true;
        const neighbours = [
          index - 1,
          index + 1,
          index - w,
          index + w,
        ];
        neighbours.forEach((next, direction) => {
          if (
            next < 0
            || next >= w * h
            || visited[next]
            || (direction === 0 && x === 0)
            || (direction === 1 && x === w - 1)
          ) return;
          if (data[next * 4 + 3] > alphaThreshold) return;
          visited[next] = 1;
          queue[tail++] = next;
        });
      }
      if (
        !touchesEdge
        && area >= 2
        && componentMinY <= connectorLimitY
        && componentMaxY >= minY
      ) {
        holeCandidates.push({
          area,
          u: sumX / area / w,
          v: sumY / area / h,
        });
      }
    };
    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        const index = y * w + x;
        if (visited[index] || data[index * 4 + 3] > alphaThreshold) continue;
        enqueueComponent(index);
      }
    }
    const topVisibleMidU = (minX + maxX + 1) / 2 / w;
    holeCandidates.sort((a, b) => (
      a.v - b.v
      || Math.abs(a.u - topU) - Math.abs(b.u - topU)
      || b.area - a.area
    ));
    const connectorHole = holeCandidates.find((candidate) => (
      candidate.u >= minX / w
      && candidate.u <= (maxX + 1) / w
      && Math.abs(candidate.u - topVisibleMidU) <= 0.32
    ));
    const fallbackSeatV = (
      minY + visibleHeight * clamp(connectorSeatDepthRatio, 0.06, 0.22)
    ) / h;
    return {
      topU,
      topV: clamp(minY / h, 0, 1),
      seatU: clamp(connectorHole?.u ?? topU, 0, 1),
      seatV: clamp(connectorHole?.v ?? fallbackSeatV, 0, 1),
      seatSource: connectorHole ? "hole" : "depth",
    };
  }

  function throughPairAttachment(material) {
    const attach = material && material.attach;
    if (!attach || attach.type !== 'through_pair' || attach.space !== 'texture'
        || !Array.isArray(attach.points) || attach.points.length < 2) return null;
    const points = attach.points.slice(0, 2).map((point) => ({
      x: Number(point && point.x),
      y: Number(point && point.y),
    }));
    if (points.some((point) => !Number.isFinite(point.x) || !Number.isFinite(point.y))) return null;
    const axisDeg = Number(attach.axis_deg);
    return {
      points,
      u: clamp((points[0].x + points[1].x) / 2, 0, 1),
      v: clamp((points[0].y + points[1].y) / 2, 0, 1),
      span: Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y),
      axisRad: (Number.isFinite(axisDeg) ? axisDeg : 0) * Math.PI / 180,
      visibleBounds: normalizedVisibleBounds(attach.visible_bounds ?? attach.visibleBounds),
      scaleMode: String(attach.scale_mode ?? attach.scaleMode ?? '').trim().toLowerCase(),
    };
  }

  function normalizedVisibleBounds(value) {
    if (!value || typeof value !== 'object') return null;
    const x = Number(value.x ?? value.left);
    const y = Number(value.y ?? value.top);
    const width = Number(value.width ?? value.w);
    const height = Number(value.height ?? value.h);
    if (![x, y, width, height].every(Number.isFinite) || width <= 0 || height <= 0) return null;
    const left = clamp(x, 0, 1);
    const top = clamp(y, 0, 1);
    const right = clamp(left + width, left, 1);
    const bottom = clamp(top + height, top, 1);
    if (!(right > left) || !(bottom > top)) return null;
    return { x: left, y: top, width: right - left, height: bottom - top };
  }

  function throughPairUsesPhysicalBounds(material, attachment = throughPairAttachment(material)) {
    return Boolean(
      attachment
      && attachment.visibleBounds
      && ['physical_bounds', 'physical-box', 'physical_box'].includes(attachment.scaleMode),
    );
  }

  function throughPairDisplayMaxMm(material) {
    const attach = throughPairAttachment(material);
    if (!attach || !(attach.span > 0)) return null;
    const item = material && typeof material === 'object' ? material : {};
    const alongMm = positive(item.widthMm, positive(item.sizeMm, 8));
    if (throughPairUsesPhysicalBounds(item, attach)) {
      const heightMm = positive(item.heightMm, alongMm);
      const bounds = attach.visibleBounds;
      // Fit the visible alpha bounds inside the real physical box. Drill-point
      // spacing controls placement only and no longer inflates the texture.
      return Math.min(alongMm / bounds.width, heightMm / bounds.height);
    }
    // The through-pair span describes the physical cylinder that sits on the
    // string, while the rest of the texture may be a much larger hanging
    // charm. Scale the full texture so that span renders at its real width.
    const safeSpan = clamp(attach.span, 0.25, 1);
    const fullTextureMm = clamp(alongMm / safeSpan, alongMm, alongMm * 3);
    const skuId = String(item.id ?? item.skuId ?? item.sku ?? '');
    const materialScale = positive(THROUGH_PAIR_DISPLAY_SCALE_BY_SKU[skuId], 1);
    return clamp(fullTextureMm * materialScale, alongMm, alongMm * 3);
  }

  function throughPairHalfExtentPx(material, {
    drawWidth = 0,
    drawHeight = 0,
    radius = 0,
  } = {}) {
    const attach = throughPairAttachment(material);
    if (!attach) return null;
    const dx = (attach.points[1].x - attach.points[0].x) * Number(drawWidth || 0);
    const dy = (attach.points[1].y - attach.points[0].y) * Number(drawHeight || 0);
    const spanPx = Math.hypot(dx, dy);
    if (!(spanPx > 0)) return null;
    // Attachment points sit just inside the cylinder ends. Retain a small
    // edge allowance without reserving the transparent hanging-charm canvas.
    const edgeAllowance = Math.max(0.5, spanPx * 0.06, positive(radius, 0) * 0.04);
    return spanPx / 2 + edgeAllowance;
  }

  function stringSeatAngle(material, slotAngle) {
    const attach = throughPairAttachment(material);
    return Number(slotAngle || 0) + Math.PI / 2 - (attach ? attach.axisRad : 0);
  }

  function curvedTextureOnString({
    x = 0,
    y = 0,
    tangentAngle = 0,
    ringRadius = 0,
    entry = null,
    middle = null,
    exit = null,
  } = {}) {
    const values = [
      x,
      y,
      tangentAngle,
      ringRadius,
      entry?.x,
      entry?.y,
      middle?.x,
      middle?.y,
      exit?.x,
      exit?.y,
    ].map(Number);
    if (values.some((value) => !Number.isFinite(value))) return null;
    const [
      stringX,
      stringY,
      targetTangent,
      requestedRadius,
      entryX,
      entryY,
      middleX,
      middleY,
      exitX,
      exitY,
    ] = values;
    const chordX = exitX - entryX;
    const chordY = exitY - entryY;
    const chordLength = Math.hypot(chordX, chordY);
    if (!(chordLength > 1e-6)) return null;
    const chordMidX = (entryX + exitX) / 2;
    const chordMidY = (entryY + exitY) / 2;
    const sourceAxis = Math.atan2(chordY, chordX);
    const outwardAngle = targetTangent - Math.PI / 2;
    const outwardX = Math.cos(outwardAngle);
    const outwardY = Math.sin(outwardAngle);
    let angle = targetTangent - sourceAxis;
    const rotate = (px, py, rotation = angle) => ({
      x: Math.cos(rotation) * px - Math.sin(rotation) * py,
      y: Math.sin(rotation) * px + Math.cos(rotation) * py,
    });
    const bulge = rotate(middleX - chordMidX, middleY - chordMidY);
    if (bulge.x * outwardX + bulge.y * outwardY < 0) angle += Math.PI;

    const radius = Math.max(chordLength / 2 + 1e-6, Math.abs(requestedRadius));
    const halfChord = chordLength / 2;
    const sagitta = radius - Math.sqrt(Math.max(0, radius * radius - halfChord * halfChord));
    const targetChordMidX = stringX - outwardX * sagitta;
    const targetChordMidY = stringY - outwardY * sagitta;
    const rotatedChordMid = rotate(chordMidX, chordMidY);
    const centerX = targetChordMidX - rotatedChordMid.x;
    const centerY = targetChordMidY - rotatedChordMid.y;
    const rotatedEntry = rotate(entryX, entryY);
    const rotatedExit = rotate(exitX, exitY);
    return {
      x: centerX,
      y: centerY,
      angle,
      halfAngle: Math.asin(Math.min(1, halfChord / radius)),
      sagitta,
      entry: { x: centerX + rotatedEntry.x, y: centerY + rotatedEntry.y },
      exit: { x: centerX + rotatedExit.x, y: centerY + rotatedExit.y },
    };
  }

  function centerTextureOnString(material, {
    x = 0,
    y = 0,
    angle = 0,
    drawWidth = 0,
    drawHeight = 0,
  } = {}) {
    const attach = throughPairAttachment(material);
    if (!attach) return { x, y };
    const localX = (attach.u - 0.5) * Number(drawWidth || 0);
    const localY = (attach.v - 0.5) * Number(drawHeight || 0);
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return {
      x: x - (cos * localX - sin * localY),
      y: y - (sin * localX + cos * localY),
    };
  }

  function stringPointFromTextureCenter(material, {
    x = 0,
    y = 0,
    angle = 0,
    drawWidth = 0,
    drawHeight = 0,
  } = {}) {
    const attach = throughPairAttachment(material);
    if (!attach) return { x, y };
    const localX = (attach.u - 0.5) * Number(drawWidth || 0);
    const localY = (attach.v - 0.5) * Number(drawHeight || 0);
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return {
      x: x + (cos * localX - sin * localY),
      y: y + (sin * localX + cos * localY),
    };
  }

  function orientThroughPairAtStringSeat(material, {
    nominal = null,
    previous = null,
    next = null,
    referenceAngle = 0,
  } = {}) {
    const attach = throughPairAttachment(material);
    if (!attach || !nominal || !previous || !next) return null;
    const values = [nominal.x, nominal.y, previous.x, previous.y, next.x, next.y].map(Number);
    if (values.some((value) => !Number.isFinite(value))) return null;
    const [nx, ny, px, py, qx, qy] = values;
    const dx = qx - px;
    const dy = qy - py;
    const lengthSq = dx * dx + dy * dy;
    if (!(lengthSq > 1e-6)) return null;
    let angle = Math.atan2(dy, dx) - attach.axisRad;
    const reference = Number(referenceAngle) || 0;
    if (Math.cos(angle - reference) < 0) angle += Math.PI;
    return {
      // The neighbouring bead chord is only an orientation reference. The
      // actual string follows the bracelet arc, so moving the attachment
      // midpoint onto the chord visibly pulls wide through-pair pieces inward.
      x: nx,
      y: ny,
      angle,
    };
  }

  // Backwards-compatible name for older cached engine builds. Its placement
  // semantics now preserve the nominal bracelet string seat.
  function alignThroughPairToChord(material, options) {
    return orientThroughPairAtStringSeat(material, options);
  }

  function estimate(items, getMaterial, { turns = 1, extraLengthMm = 0 } = {}) {
    let totalWidthMm = 0;
    let weightedThicknessMm = 0;
    for (const item of Array.isArray(items) ? items : []) {
      const geometry = materialGeometry(getMaterial(item));
      totalWidthMm += geometry.alongMm;
      weightedThicknessMm += geometry.alongMm * geometry.thicknessMm;
    }
    const avgThicknessMm = totalWidthMm > 0 ? weightedThicknessMm / totalWidthMm : 0;
    const componentLengthMm = Math.max(0, Number(extraLengthMm) || 0);
    const finishedTotalWidthMm = totalWidthMm + componentLengthMm;
    const perLoopMm = Number(turns) === 2
      ? (finishedTotalWidthMm - WRAP_CROSS_MM) / 2
      : finishedTotalWidthMm;
    // The bead span is the bracelet centreline circumference. Subtract the
    // average bead diameter once to estimate the wearable inner circumference.
    // A short, incomplete strand can legitimately have no closed inner opening;
    // clamp that physical result at zero instead of borrowing the tray's visual
    // minimum circumference.
    const centerlineCircumferenceMm = Math.max(0, perLoopMm);
    const innerCircumferenceMm = Math.max(0, centerlineCircumferenceMm - Math.PI * avgThicknessMm);
    return {
      totalWidthMm,
      componentLengthMm,
      finishedTotalWidthMm,
      avgThicknessMm,
      innerCircumferenceMm,
      wristCm: innerCircumferenceMm / 10,
    };
  }

  return {
    MIN_CIRCUMFERENCE_MM,
    WRAP_CROSS_MM,
    ELASTIC_CLOSE_INNER_MM,
    estimate,
    elasticCircleLayout,
    materialGeometry,
    layoutHalfExtentPx,
    pendantSlotWidthMm,
    detectPendantAnchorFromAlpha,
    throughPairAttachment,
    normalizedVisibleBounds,
    throughPairUsesPhysicalBounds,
    throughPairDisplayMaxMm,
    throughPairHalfExtentPx,
    stringSeatAngle,
    curvedTextureOnString,
    centerTextureOnString,
    stringPointFromTextureCenter,
    orientThroughPairAtStringSeat,
    alignThroughPairToChord,
  };
}));
